import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { PacksListQueryParam } from 'app/components/pages/Packs';
import type {
  Package,
  PackageCopywriting,
  PackageCover,
  PackageDisclaimer,
  PackageDiscover,
  PackageListItem,
} from 'app/typings/packages';
import type { PageMetaData } from 'app/typings/pagination';
import { apiRequest } from 'app/utils/request/api';

export const listPath = '/packages' as const;
export const listPaginatedPath = '/packages/paginated' as const;

export const packagePath = '/packages/:packageId' as const;
export const packageCopywritingPath =
  'packages/:packageId/copywriting' as const;
export const packageDiscoveryPath = 'packages/:packageId/discover' as const;
export const packageCoverPath = 'packages/:packageId/cover' as const;
export const packageDisclaimersPath =
  'packages/:packageId/disclaimers' as const;

export function usePackages(dayPackage?: boolean, isEnabled?: boolean) {
  return useQuery({
    queryKey: [listPath, { dayPackage }],
    queryFn: () =>
      apiRequest({
        path: listPath,
        queryParams: { dayPackage },
      }) as Promise<PackageListItem[]>,
    enabled: isEnabled,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
  });
}

export function usePaginatedPackages(queryParams: PacksListQueryParam) {
  const key = useMemo(() => {
    return [
      listPaginatedPath,
      {
        queryParams,
      },
    ];
  }, [queryParams]);

  return useQuery({
    queryKey: key,
    queryFn: () =>
      apiRequest({ path: listPaginatedPath, queryParams }) as Promise<{
        packages: PackageListItem[];
        pagination: PageMetaData;
      }>,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    placeholderData: keepPreviousData,
  });
}

export function useGetPackage(packageId?: number | null) {
  return useQuery({
    queryKey: [packagePath, { packageId }],
    queryFn: packageId
      ? () =>
          apiRequest({
            path: packagePath,
            params: {
              packageId: packageId.toString(),
            },
          }) as Promise<Package>
      : skipToken,
    enabled: !!packageId,
    refetchOnWindowFocus: false,
    meta: { errorMessage: 'Error fetching package' },
  });
}

export function useGetPackageCopywriting(packageId: number | null) {
  return useQuery({
    queryKey: [packageCopywritingPath, [packageId]],
    queryFn: packageId
      ? () =>
          apiRequest({
            path: packageCopywritingPath,
            params: {
              packageId: packageId.toString(),
            },
          }) as Promise<PackageCopywriting>
      : skipToken,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!packageId,
    meta: { errorMessage: 'Error fetching package copywriting' },
  });
}

export function useGetPackageDiscover(packageId: number | null) {
  return useQuery({
    queryKey: [packageDiscoveryPath, [packageId]],
    queryFn: packageId
      ? () =>
          apiRequest({
            path: packageDiscoveryPath,
            params: {
              packageId: packageId.toString(),
            },
          }) as Promise<PackageDiscover>
      : skipToken,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!packageId,
    meta: { errorMessage: 'Error fetching package copywriting' },
  });
}

export function useGetPackageCover(packageId: number | null) {
  return useQuery({
    queryKey: [packageCoverPath, [packageId]],
    queryFn: packageId
      ? () =>
          apiRequest({
            path: packageCoverPath,
            params: {
              packageId: packageId.toString(),
            },
          }) as Promise<PackageCover>
      : skipToken,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!packageId,
    meta: { errorMessage: 'Error fetching package copywriting' },
  });
}

export function useGetPackageDisclaimer(packageId: number | null) {
  return useQuery({
    queryKey: [packageDisclaimersPath, [packageId]],
    queryFn: packageId
      ? () =>
          apiRequest({
            path: packageDisclaimersPath,
            params: {
              packageId: packageId.toString(),
            },
          }) as Promise<PackageDisclaimer[]>
      : skipToken,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!packageId,
    meta: { errorMessage: 'Error fetching package disclaimers' },
  });
}
